import { useEffect } from 'react';
import { useMap } from 'react-leaflet';

/**
 * Forces Leaflet to recompute its size once the container actually has
 * dimensions, and again whenever that container resizes.
 *
 * Why: Leaflet measures its container at init. On mobile the map often
 * mounts before layout settles (vh units resolving as the browser chrome
 * collapses, tab/accordion reveal, orientation change), so it initializes
 * with zero/wrong height and never paints tiles — a white screen. Toggling
 * fullscreen "fixed" it only because that dispatched a resize event. This
 * makes the recompute happen unconditionally, everywhere.
 */
export function EnsureMapSize() {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const invalidate = () => {
      try {
        map.invalidateSize(false);
      } catch {
        /* map torn down mid-frame — ignore */
      }
    };

    // Recompute after the first frame, once the container's real size is
    // known. A second deferred pass covers mobile address-bar collapse.
    const raf = window.requestAnimationFrame(invalidate);
    const timer = window.setTimeout(invalidate, 250);

    const container = map.getContainer();
    const observer = new ResizeObserver(invalidate);
    observer.observe(container);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [map]);

  return null;
}

export default EnsureMapSize;
